import { useEffect, useRef, useState } from "react";

const PUBLIC_LINKS = [
  { path: "/", label: "Home" },
  { path: "/our-why", label: "Our Why" },
  { path: "/active", label: "Login" },
];

const AUTHENTICATED_TABS = [
  { id: "active", label: "Active Conversations", path: "/active" },
  { id: "past", label: "Past Conversations", path: "/past" },
  {
    id: "connect",
    label: "Browse and Connect",
    path: "/connect",
    walkthroughId: "nav-connect",
  },
];

const AUTHENTICATED_MENU_ITEMS = [
  { id: "profile", label: "Update Profile", path: "/edit-profile" },
  { id: "settings", label: "Settings", path: "/settings" },
  { id: "contact", label: "Contact Us", path: "/contact" },
];

function findScrollableAncestor(element) {
  let node = element;

  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const isScrollable = /(auto|scroll|overlay)/.test(style.overflowY || "");

    if (isScrollable && node.scrollHeight > node.clientHeight) {
      return node;
    }

    node = node.parentElement;
  }

  return null;
}

function readScrollTop(root) {
  if (!root) {
    return window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  return root.scrollTop || 0;
}

export function resolveScrolledState({
  isScrolled = false,
  scrollTop = 0,
  enterThreshold = 12,
  exitThreshold = 4,
} = {}) {
  const currentScrollTop = Math.max(0, Number(scrollTop) || 0);

  if (!isScrolled && currentScrollTop >= enterThreshold) {
    return true;
  }

  if (isScrolled && currentScrollTop <= exitThreshold) {
    return false;
  }

  return isScrolled;
}

function isPrefixMatch(currentPath, linkPath) {
  return currentPath === linkPath || currentPath.startsWith(`${linkPath}/`);
}

// Returns the public navigation links for the landing and marketing routes.
export function getPublicLinks() {
  return PUBLIC_LINKS.map((link) => ({ ...link }));
}

// Returns the authenticated primary tabs and injects any active conversation badge state.
export function getAuthenticatedTabs({ hasActiveNotifications = false } = {}) {
  return AUTHENTICATED_TABS.map((tab) => ({
    ...tab,
    hasNotification: tab.id === "active" ? Boolean(hasActiveNotifications) : false,
  }));
}

// Returns the authenticated account menu items with route-aware active state for rendering.
export function getAuthenticatedMenuItems(currentPath) {
  return AUTHENTICATED_MENU_ITEMS.map((item) => ({
    ...item,
    isActive:
      item.match === "prefix"
        ? isPrefixMatch(currentPath, item.path)
        : isActivePath(currentPath, item.path),
  }));
}

// Returns the brand destination (always the public landing page).
export function getBrandTarget() {
  return "/";
}

// Checks whether a route should be treated as active for exact-match navigation styling.
export function isActivePath(currentPath, linkPath) {
  return currentPath === linkPath;
}

// Chooses the best user-facing name for navigation account UI.
export function getUserDisplayName(userName, userEmail) {
  return userName || userEmail || "";
}

// Collapses a full display name to the short account label shown in the desktop trigger.
export function getAccountLabel(displayName) {
  return (
    String(displayName || "Account")
      .trim()
      .split(/\s+/)
      .filter(Boolean)[0] || "Account"
  );
}

// Detects modified clicks so browser-native new-tab and open-in-background behavior is preserved.
export function isModifiedNavigationEvent(event) {
  return (
    event.button !== 0 ||
    event.metaKey ||
    event.altKey ||
    event.ctrlKey ||
    event.shiftKey
  );
}

// Runs the shared leave guard before navigation or account actions that might discard work.
export function attemptNavigationWithGuard({ from = "", to = "", proceed }) {
  if (typeof proceed !== "function") {
    return false;
  }

  const event = new CustomEvent("app:attempt-leave", {
    cancelable: true,
    detail: {
      from: String(from || ""),
      to: String(to || ""),
      proceed,
    },
  });

  window.dispatchEvent(event);

  if (event.defaultPrevented) {
    return false;
  }

  proceed();
  return true;
}

// Tracks whether the nav should render in its compact scrolled state for the current route.
export function useNavigationScrollState(pathname) {
  const [isScrolled, setIsScrolled] = useState(false);
  const isScrolledRef = useRef(false);

  useEffect(() => {
    const setScrolled = (nextValue) => {
      if (nextValue !== isScrolledRef.current) {
        isScrolledRef.current = nextValue;
        setIsScrolled(nextValue);
      }
    };

    const sentinel = document.getElementById("nav-scroll-sentinel");

    if (!sentinel) {
      setScrolled(false);
      return undefined;
    }

    const root = findScrollableAncestor(sentinel.parentElement);
    const onScroll = () =>
      setScrolled(
        resolveScrolledState({
          isScrolled: isScrolledRef.current,
          scrollTop: readScrollTop(root),
        })
      );
    const target = root || window;

    target.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => target.removeEventListener("scroll", onScroll);
  }, [pathname]);

  return isScrolled;
}
